// ═══════════════════════════════════════════════════════════════════
// REPORTS DATA CONFIGURATION
// ═══════════════════════════════════════════════════════════════════
// This is the central configuration for all Budmen Education Program Reports.
// To add a new report:
// 1. Add a new entry to REPORTS_CONFIG with a unique slug
// 2. Fill in all required fields following the existing patterns
// 3. The report will automatically be available at /reports/[slug]

export const REPORTS_CONFIG = {
  // Watts the Spot - Multi-city energy program
  "watts-the-spot-2026": {
    slug: "watts-the-spot-2026",
    password: "watts2026",
    heroImage: "/images/Budmen-Watts-students.webp", // Optional hero background image
    heroOverlay: "dark", // Options: "light", "medium", "dark" (default: "dark")
    program: {
      name: "Watts the Spot",
      tagline: "Collaboratively Creating Power Beacons That Keep Us Connected",
      partner: "MACNY Partners in Education + Business",
      dates: "October 2025 – February 2026",
      locations: "Rochester · Syracuse · Binghamton",
      studentsServed: "30+",
      teamsFormed: 9,
      sessionsDelivered: "21",
      hoursOfInstruction: "50+",
      reportDate: "February 2026",
      cityCohorts: [
        { city: "Binghamton", students: "20" },
        { city: "Rochester", students: "10" },
        { city: "Syracuse", students: "10" },
      ],
    },
    drivingQuestion:
      "What if a group of students could design renewable power solutions for the places in their community that need them most?",
    overview:
      "Watts the Spot expanded across three cities this fall and winter. In each city, high school students treated their community as a living design brief. They identified real locations where dependable power is a safety or equity issue, then engineered portable, renewable Power Beacon prototypes to address them. Every cohort moved through seven sessions: from challenge introduction through problem identification, component exploration, solution proposals, fast iteration prototyping, finalization, and a public Learning Showcase.",

    // Images from WTS-Winter26 photo set
    images: [
      {
        src: "/images/WTS-Winter26-08.webp",
        caption: "Students exploring renewable energy solutions",
        aspect: "3/2",
      },
      {
        src: "/images/WTS-Winter26-11.webp",
        caption: "Team collaboration on Power Beacon prototypes",
        aspect: "3/2",
      },
      {
        src: "/images/WTS-Winter26-14.webp",
        caption: "Testing piezoelectric components",
        aspect: "3/2",
      },
      {
        src: "/images/WTS-Winter26-15.webp",
        caption: "Solar panel integration workshop",
        aspect: "3/2",
      },
      {
        src: "/images/WTS-Winter26-16.webp",
        caption: "Student prototyping Power Beacons",
        aspect: "3/2",
      },
      {
        src: "/images/WTS-Winter26-17.webp",
        caption: "Community mapping exercise",
        aspect: "3/2",
      },
      {
        src: "/images/WTS-Winter26-18.webp",
        caption: "Prototype development in action",
        aspect: "3/2",
      },
      {
        src: "/images/WTS-Winter26-19.webp",
        caption: "Student working with Budmen Inquiry Mentor",
        aspect: "3/2",
      },
    ],

    // City profiles with game data
    // Removed redundant intro - the game instructions are sufficient

    // Connections Game for city profiles
    connectionsGame: {
      groups: [
        {
          id: "renewable-tech",
          name: "Ways to Generate Power",
          cards: ["Solar panels", "Dynamos", "Piezoelectric", "LEDs"],
          color: "#4ECDC4",
          difficulty: 1,
          insight:
            "Students learned renewable energy technology by wiring real components into working circuits. They held solar panels, piezoelectric elements, hand-crank dynamos, and LEDs, connected them, and watched them work. This hands-on understanding became the technical foundation for every Power Beacon.",
        },
        {
          id: "student-prototypes",
          name: "Student Prototype Features",
          cards: ["Stadium Stairs", "Merry Go Round", "Bus Stop", "Drawbridge"],
          color: "#FF6B6B",
          difficulty: 2,
          insight:
            "These are actual features students designed into their Power Beacon prototypes. Each team imagined how their beacon would physically integrate into a real location: stairs that harvest footsteps, a merry-go-round that generates power through play, a bus stop with built-in solar charging, a drawbridge for access and visibility.",
        },
        {
          id: "design-choices",
          name: "Design Choices to Invite Engagement",
          cards: [
            "Splat Icon",
            "Words of Affirmation",
            "Bright Red Color",
            "Duck Shape",
          ],
          color: "#95E77E",
          difficulty: 3,
          insight:
            "Every team had to answer a question most engineers never ask: how do you get a stranger to walk up and use this thing? Students designed playful visual language to make their Power Beacons approachable. This is where engineering met communication design, and where students discovered that a great solution nobody uses is no solution at all.",
        },
        {
          id: "real-world",
          name: "Community Need-Gap Locations",
          cards: [
            "Last Bus Stop",
            "Late Shift Lockout",
            "The Big Game",
            "Snow Storm Pickup",
          ],
          color: "#FFE66D",
          difficulty: 4,
          insight:
            "These are real moments students identified through community investigation where a dead phone becomes a safety issue. A rider stranded at the last stop. A worker locked out after a late shift. A parent coordinating pickup in a snowstorm. Every Power Beacon was designed to solve one of these moments.",
        },
      ],
    },

    cityProfiles: [
      {
        city: "Binghamton",
        accent: "#E85D2A",
        studentsCount: "~20 students",
        summary:
          "The Binghamton cohort circled around the theme of augmenting existing infrastructure, literally building piezoelectric generators into stadium stairs and integrating solar panels into bus stop structures. This group embraced joy as a design principle — students wrote words of affirmation directly onto their prototype stairs, and designed bold splat graphics in red to signal to passersby: stop here, this is something. Their Power Beacons weren't just functional; they were invitations.",
        highlights: [
          "Piezoelectric elements embedded in stadium stairways: every footstep becomes a power source",
          "Solar integrated bus stop concepts that doubled as community gathering markers",
          "Playful, abstract iconography designed to make energy generation feel like a game",
        ],
        theme: "Infrastructure as Playground",
      },
      {
        city: "Rochester",
        accent: "#2A7DE8",
        studentsCount: "~10 students",
        summary:
          "The Rochester students impressed us with the breadth and depth of their systems thinking. They pushed past environmental and logistical conditions into the social, psychological, and cultural factors shaping energy access in their neighborhoods, asking not just who needs power and where, but why those gaps exist and what community trust actually looks like in a working solution. One group built a merry-go-round into their prototype, designing for the whole family. Another group articulated something that stopped us: 'the biggest challenge was identifying the number of considerations we had to make.' That's not just good design thinking, that's the rare ability to see the full shape of a complex problem while you're inside it.",
        highlights: [
          "Deepest problem investigation of all three cohorts: social, psychological, and cultural analysis alongside technical",
          "Experiments that tested not just circuit performance but community trust and adoption likelihood",
          "Solutions that accounted for cultural context, not just engineering constraints",
        ],
        theme: "Inquiry Beyond the Circuit",
      },
      {
        city: "Syracuse",
        accent: "#1A8A7D",
        studentsCount: "~10 students",
        summary:
          "Syracuse was our most hands-on group of students, who started to discover their voices in the form of building. This cohort had by far the most expressive and engaging iconography, designs, and solutions of all three cities, including a Power Beacon that looked like a duck. Their prototypes didn't just work; they communicated personality, humor, and care in every detail.",
        highlights: [
          "The most expressive prototypes across all three cities: design as personal voice",
          "A Power Beacon shaped like a duck that became the cohort's signature creation",
          "Students found confidence through making; building became their form of expression",
        ],
        theme: "Building as Voice",
      },
    ],

    // Facilitator observations with trivia game data
    // Removed redundant intro - section heading is self-explanatory

    // Trivia Game for facilitator observations
    triviaGame: {
      questions: [
        {
          question:
            "The same program framework ran in all three cities. How many distinct design philosophies emerged?",
          options: [
            "One — the framework produces consistent results",
            "Two — one urban, one suburban",
            "Three — each city developed its own character",
            "None — the cohorts were too small to develop identities",
          ],
          correctIndex: 2,
          insight:
            "Three cities, three personalities. The Creative Discovery Method creates consistent structure while leaving room for each group's authentic character to emerge. Binghamton leaned into playfulness, Rochester into deep inquiry, Syracuse into expressive making. Same seven sessions, three completely different flavors of innovation.",
        },
        {
          question:
            "Binghamton students designed Power Beacons that were embedded into existing structures. What design principle does this represent?",
          options: [
            "Minimalism — using fewer materials",
            "Infrastructure augmentation — reimagining how the built environment serves people",
            "Camouflage — hiding technology from view",
            "Standardization — making one design fit everywhere",
          ],
          correctIndex: 1,
          insight:
            "Binghamton's instinct to embed energy generation into existing infrastructure like stairs, bus stops, and gathering spaces represents sophisticated systems thinking. They weren't designing standalone devices; they were reimagining how the built environment could actively serve people. That's a paradigm shift you can't teach from a textbook.",
        },
        {
          question:
            "The Syracuse cohort's most memorable prototype was a Power Beacon shaped like which animal?",
          options: ["An owl", "A duck", "A squirrel", "A turtle"],
          correctIndex: 1,
          insight:
            "The duck Power Beacon wasn't just clever; it was a student saying 'I have something to say, and this is how I say it.' The Syracuse cohort's journey was one of the most satisfying to witness. Students who were uncertain at the start found their confidence not through discussion or worksheets, but through the act of building. When hands lead, voices follow.",
        },
        {
          question:
            "Which factor did the facilitators identify as most closely linked to deeper student engagement from Session 1 forward?",
          options: [
            "Smaller cohort size",
            "Pre-program orientation — students knowing what they signed up for",
            "Access to more components",
            "Having an industry mentor present",
          ],
          correctIndex: 1,
          insight:
            "Rochester and Binghamton students had been better informed ahead of Session 1 about the program's purpose and structure. We believe this directly contributed to deeper and more sustained engagement across the full program arc. Students who knew what they had signed up for arrived ready to dig in, while those who were unsure spent early sessions orienting rather than investigating.",
        },
      ],
    },

    facilitatorObservations: [
      {
        theme: "Three Cities, Three Personalities",
        observation:
          "Running the same program framework across three cities revealed something powerful: the Creative Discovery Method creates consistent structure while leaving room for each group's authentic character to emerge. Binghamton leaned into playfulness, Rochester into deep inquiry, Syracuse into expressive making. Same seven sessions, three completely different flavors of innovation.",
      },
      {
        theme: "Infrastructure Augmentation as a Design Pattern",
        observation:
          "Binghamton's instinct to embed energy generation into existing infrastructure like stairs, bus stops, and gathering spaces represents sophisticated systems thinking. They weren't designing standalone devices; they were reimagining how the built environment could actively serve people. That's a paradigm shift you can't teach from a textbook.",
      },
      {
        theme: "When Hands Lead, Voices Follow",
        observation:
          "The Syracuse cohort's journey was one of the most satisfying to witness. Students who were uncertain at the start found their confidence not through discussion or worksheets, but through the act of building. The duck Power Beacon wasn't just clever; it was a student saying 'I have something to say, and this is how I say it.'",
      },
      {
        theme: "Depth of Inquiry Correlates with Preparation",
        observation:
          "The Rochester and Binghamton students arrived to Session 1 with a clearer sense of what the program was and why they were there and it showed. Those cohorts engaged more quickly and went deeper across the full program arc. Students who knew what they had signed up for arrived ready to dig in, while those who were less informed spent early sessions orienting rather than investigating.",
      },
    ],

    improvements: [
      {
        area: "Student Retention Across the Arc",
        detail:
          "We saw a few students drop off in most cities. We attribute this primarily to weather cancellations and the juggling of dates around three distinct holiday periods: Thanksgiving, December holidays/New Year's, and Valentine's Day. A multi-month Saturday program that spans October to February inevitably collides with winter weather and family schedules. Future iterations should either compress the timeline or build explicit 'catch-up' mechanisms so a missed session doesn't become a permanent exit.",
      },
      {
        area: "Pre-Program Orientation Matters",
        detail:
          "Rochester and Binghamton students arrived better informed about what the program was, why they were there, and what they'd be doing. This resulted in noticeably deeper and more sustained engagement from Session 1 forward. Syracuse students, by contrast, were uncertain at the first sessions about what they had signed up for. Going forward, we recommend a standardized pre-program communication packet: a short video, a one-page overview, and a 'what to expect' message sent to every student before the first session. First impressions set the trajectory.",
      },
    ],

    // Outcomes with Memory Match game data
    // Removed redundant intro - outcomes speak for themselves

    // Memory Match game for outcomes
    memoryMatchGame: {
      pairs: [
        {
          cardA: "☀️",
          cardB: "Solar panel",
          explanation:
            "Students wired real solar panels into their Power Beacon prototypes, learning firsthand how photovoltaic cells convert light into usable electricity.",
        },
        {
          cardA: "👟",
          cardB: "Piezoelectric element",
          explanation:
            "Binghamton students embedded piezoelectric elements into stadium stairways. Every footstep generates a small charge, turning human movement into renewable power.",
        },
        {
          cardA: "✏️",
          cardB: "Blueprint",
          explanation:
            "Before building anything, every team sketched their Power Beacon concept on blueprint worksheets. They mapped the problem, the stakeholders, the solution, and how it would work in the real world.",
        },
        {
          cardA: "📦",
          cardB: "Prototype",
          explanation:
            "Cardboard was the primary prototyping material. Students built, tested, broke, and rebuilt their Power Beacons, learning through fast iteration that failure is data, not defeat.",
        },
        {
          cardA: "🗺️",
          cardB: "Community",
          explanation:
            "Students treated their cities as living design briefs. They mapped real locations where dependable power is a safety or equity issue and grounded every design decision in what they found.",
        },
        {
          cardA: "🎤",
          cardB: "Showcase",
          explanation:
            "Every cohort culminated in a public Learning Showcase where teams presented their Power Beacons with feasibility and community impact analysis to real audiences.",
        },
        {
          cardA: "🔋",
          cardB: "Phone charger",
          explanation:
            "The driving motivation behind every Power Beacon: a phone at one percent isn't just an inconvenience. It can mean a parent misses an emergency alert or a storm-relief volunteer can't open the map that guides families to safety.",
        },
        {
          cardA: "💪",
          cardB: "Dynamo",
          explanation:
            "Students experimented with hand-crank dynamos to generate electricity through motion — turning physical effort into stored power and understanding the direct relationship between energy input and output.",
        },
      ],
    },

    outcomes: [
      "Students can identify community power equity issues and articulate why they matter",
      "Students can wire functional circuits combining solar, piezoelectric, and dynamo energy sources",
      "Students can present technical solutions with feasibility and community impact analysis",
      "Students can adapt design approaches to local context and infrastructure",
      "Students can investigate problems beyond surface symptoms to find root causes",
      "Students can express technical solutions with personal voice and cultural relevance",
      "Students can fail fast, iterate, and persist through technical challenges",
    ],

    lookingAhead: {
      intro: "We recommend three actions:",
      actions: [
        'Standardize pre-program orientation. Create a communication packet for all future multi-city cohorts — a short video, a one-page overview, and a "what to expect" message sent to every student before Session 1.',
        "Explore scheduling models that maintain weekly continuity across multiple cities. Eight consecutive weeks in a single city kept attendance strong. Expanding to three cities introduced holiday and weather gaps that broke that rhythm. Future iterations should prioritize consistent week-to-week cadence across all locations.",
        "Document the three city profiles as case studies. The fact that the same framework produced three distinct design cultures is itself a powerful proof point for the Creative Discovery Method's adaptability.",
      ],
    },

    whatsNext:
      "We recommend three actions: First, standardize a pre-program orientation packet for all future multi-city cohorts. Second, explore a compressed timeline (8–10 weeks vs. 16+) to reduce holiday-related attrition. Third, document the three city profiles as case studies — the fact that the same framework produced three distinct design cultures is itself a powerful proof point for the Creative Discovery Method's adaptability.",
  },

  // From Atoms to Layers - SUNY Poly 3-day camp
  "sunypoly-feb2026": {
    slug: "sunypoly-feb2026",
    password: "layers2026",
    heroImage: "/images/SUNYPOLY-Feb29-03.webp", // Optional hero background image
    program: {
      name: "From Atoms to Layers",
      tagline: "Exploring the digital fabrication revolution together!",
      partner: "SUNY Polytechnic Institute — Pre-Collegiate Outreach",
      partnerContact: "John Reade, Director of Pre-Collegiate Outreach",
      dates: "February 17–19, 2026",
      location: "SUNY Poly Campus",
      studentsServed: 20,
      daysDelivered: 3,
      hoursPerDay: "2.5 instructional + setup/packout",
      reportDate: "February 2026",
      partnerOrg: "Hillside",
    },
    // Custom stats for this report
    customStats: [
      { number: 20, label: "Students" },
      { number: 3, label: "Days" },
      { number: 4, label: "Hillside Mentors" },
      { number: 1, label: "Legendary Cheese Whiz Dragon" },
    ],
    drivingQuestion:
      "What happens when students discover that a 3D printer doesn't just make objects, it's a revolution in access?",
    overview:
      "From Atoms to Layers was a three-day Creative Discovery camp designed to help students understand the revolutionary power of digital fabrication: translating the physical world into digital information and then turning that information back into physical form. Twenty students from Hillside experienced additive manufacturing, 3D scanning, and the revolution of access that 3D printing provides — all grounded in the Creative Discovery Method.",

    // Images from SUNYPOLY-Feb29 photo set
    images: [
      {
        src: "/images/SUNYPOLY-Feb29-03.webp",
        caption: "Students using hot glue to learn layering strategies",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Feb29-04.webp",
        caption: "The cheese whiz dragon — a moment of glorious failure",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Feb29-05.webp",
        caption: "Hands-on layering experiments with four materials on Day 2",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Feb29-06.webp",
        caption: "Live 3D scanning demonstration with student volunteers",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Feb29-07.webp",
        caption: "Clay concepts taking shape on Day 3",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Feb29-08.webp",
        caption: "Students extruding their designs in real 3D filament",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Feb29-09.webp",
        caption: "A student translating their design from clay to polymer",
        aspect: "3/2",
      },
    ],

    // Removed redundant intro - the day-by-day narrative is self-contained

    // Connections Game for Day-by-Day section
    connectionsGame: {
      groups: [
        {
          id: "making-layers",
          name: "Making from Layers",
          cards: ["Clay", "Cheese whiz", "Hot glue", "Whipped cream"],
          color: "#4ECDC4",
          difficulty: 1,
          insight:
            "Students explored additive manufacturing by attempting to recreate objects using four wildly different materials, each with its own structural properties and failure modes. The spectacular cheese whiz dragon became the camp's best lesson in why material choice matters.",
        },
        {
          id: "capturing-world",
          name: "Capturing the Physical World",
          cards: ["3D scanner", "Camera", "Infrared sensor", "Photos"],
          color: "#FF6B6B",
          difficulty: 2,
          insight:
            "Students experienced two different methods for turning physical objects into digital data: infrared scanning and photogrammetry, with two student volunteers scanned live and the results compared as a class. They then used plastic extrusion devices to build their original clay concepts in real 3D printing filament, closing the loop between the physical and digital worlds.",
        },
        {
          id: "additive-manufacturing",
          name: "Hands On with Additive Manufacturing",
          cards: ["Filament", "Extruder", "Infill", "Layer Height"],
          color: "#95E77E",
          difficulty: 3,
          insight:
            "Students got their hands dirty with the real mechanics of how 3D printing works. Layering, extrusion, infill patterns, layer height. These aren't vocabulary words from a textbook. Students experimented with these concepts using cheese whiz, hot glue, clay, and whipped cream before ever touching a 3D printer. By the time they extruded real filament on Day 3, they already understood why layer height matters and what happens when infill isn't enough to hold a structure together.",
        },
        {
          id: "real-world-apps",
          name: "Real-World Applications",
          cards: [
            "Manufacturing",
            "Prototyping",
            "Iteration",
            "Problem-solving",
          ],
          color: "#FFE66D",
          difficulty: 4,
          insight:
            "The skills students practiced over three days (layering materials, analyzing trade-offs, iterating through failure, and turning ideas into physical objects) are the same skills used every day in modern manufacturing and engineering. This wasn't a classroom exercise; it was a window into how real things get made.",
        },
      ],
    },

    dayByDay: [
      {
        day: 1,
        title: "Applied Curiosity + The Joy of Making",
        date: "Monday, February 17",
        content:
          "We opened with an introduction to Applied Curiosity and the Creative Discovery Method, not as a lecture, but as an experience. Students dove into the Mystery Box Challenge, a collaboration game that builds observation and hypothesis skills by actively challenging assumptions. Then came the Budmen Scribblers: students assembled their own drawing robots from scratch, learning basic electronics and assembly in the process, and took them home. The energy was immediate. Students were laughing, competing, and problem-solving within minutes of walking through the door.",
        keyMoment:
          "The Scribblers were the perfect on-ramp. Low barrier, high delight, and every student left Day 1 having built something real with their own hands.",
      },
      {
        day: 2,
        title: "Why 3D Printing Is a Revolution",
        date: "Tuesday, February 18",
        content:
          "Day 2 opened with interactive discussions about why additive manufacturing matters: not just as a technology, but as a paradigm shift. We explored the democratization of technology, the liberation of access, and the fundamental transition of physical objects from atoms to bits. Then students got their hands dirty, literally. Using cheese whiz, hot glue, clay, and whipped cream, they attempted to recreate four very different objects with distinct structural challenges. At each stage, students reflected on what went well, what failed, and how they'd approach it differently, embedding the iterative process as a practice, not just a concept.",
        keyMoment:
          "The cheese whiz dragon was one for the history books. A student attempted to build a complex form using only pressurized cheese, and the spectacular, hilarious failure became the day's best lesson in material properties, structural integrity, and the value of laughing through failure.",
      },
      {
        day: 3,
        title: "From Inspiration to Digital Creation",
        date: "Wednesday, February 19",
        content:
          "We brought it all together on the final day. We began with a live 3D scanning demonstration using both infrared and photogrammetry captures, with two student volunteers stepping up to be scanned. The class then compared the results of both methods together, analyzing the strengths and weaknesses of each technology and what each one sees (and misses) in the physical world. From there, students created their own original concepts in clay, then used plastic extrusion devices to build their objects out of real 3D printing filament, acting as human 3D printers. They were deeply engaged, and the creations were fantastic.",
        keyMoment:
          "Watching students go from analyzing how scanning technology captures the physical world to physically extruding their own designs in real filament was the moment the connection between digital and physical fabrication stopped being abstract and became something they could hold in their hands.",
      },
    ],

    // Removed redundant intro - section heading is clear

    // Trivia Game for Facilitator Observations
    triviaGame: {
      questions: [
        {
          question:
            "The Budmen education team described this group's engagement level as which of the following?",
          options: [
            "Strong on Day 1, tapering off by Day 3",
            "Quiet but focused throughout",
            "Remarkably engaged from minute one with energy that carried all three days without dropping off",
            "Variable depending on the activity",
          ],
          correctIndex: 2,
          insight:
            "This was a remarkably engaged group of students. The Budmen education team had a blast all three days. There was a palpable sense of belonging and excitement in the room that carried from Monday morning through Wednesday afternoon without dropping off.",
        },
        {
          question:
            "Four adult mentors came from Hillside with the students. What role did they play during the camp?",
          options: [
            "They observed quietly from the back of the room",
            "They managed behavior and logistics",
            "They were active learners alongside the students, changing the entire dynamic",
            "They led some of the technical instruction",
          ],
          correctIndex: 2,
          insight:
            "A huge amount of credit goes to the four adult mentors who came from Hillside with the students. We know that when students spend time in environments with adults who genuinely care, create an authentic place of belonging, participate in authentic learning and lifelong learning, and model genuine empathy, the result is children who are resilient, confident, and engaged. That's exactly what we saw here. The mentors weren't passive chaperones. They were active learners alongside the students, and that changed the entire dynamic.",
        },
        {
          question:
            "The layering fabrication challenge on Day 2 was designed to produce a specific outcome. What was it?",
          options: [
            "Perfect replicas of the target objects",
            "A competition to find the best material",
            "Failure: glorious, reflective, instructive failure",
            "Speed and efficiency under time pressure",
          ],
          correctIndex: 2,
          insight:
            "The layering fabrication challenge on Day 2 was designed to produce failure, and it did, gloriously. But because the reflection was built into every stage, students didn't experience failure as defeat. They experienced it as data. By the time they reached the 3D printing on Day 3, they had already internalized the iterative mindset: try, observe, reflect, adjust.",
        },
        {
          question:
            "When students learned about the transition from atoms to bits (the idea that physical objects can become information) what did the facilitators observe?",
          options: [
            "Polite interest but not much connection",
            "Confusion about the technical concepts",
            "Lightbulbs going on: a new way of thinking about ideas and the physical world",
            "Strong engagement only from students with prior tech experience",
          ],
          correctIndex: 2,
          insight:
            "When we talked about the transition from atoms to bits (the idea that physical objects can become information and information can become physical objects) you could see the lightbulbs going on. For students who hadn't been exposed to digital fabrication before, this wasn't just a new tool. It was a new way of thinking about the relationship between ideas and the physical world.",
        },
      ],
    },

    facilitatorObservations: [
      {
        theme: "Remarkably Engaged from Minute One",
        observation:
          "This was a remarkably engaged group of students. The Budmen education team had a blast all three days. There was a palpable sense of belonging and excitement in the room that carried from Monday morning through Wednesday afternoon without dropping off.",
      },
      {
        theme: "The Hillside Mentors Made the Difference",
        observation:
          "A huge amount of credit goes to the four adult mentors who came from Hillside with the students. We know that when students spend time in environments with adults who genuinely care, create an authentic place of belonging, participate in authentic learning and lifelong learning, and model genuine empathy, the result is children who are resilient, confident, and engaged. That's exactly what we saw here. The mentors weren't passive chaperones. They were active learners alongside the students, and that changed the entire dynamic.",
      },
      {
        theme: "Failure as Fuel",
        observation:
          "The layering fabrication challenge on Day 2 was designed to produce failure, and it did, gloriously. But because the reflection was built into every stage, students didn't experience failure as defeat. They experienced it as data. By the time they reached the 3D printing on Day 3, they had already internalized the iterative mindset: try, observe, reflect, adjust.",
      },
      {
        theme: "The Paradigm Shift Landed",
        observation:
          "When we talked about the transition from atoms to bits (the idea that physical objects can become information and information can become physical objects) you could see the lightbulbs going on. For students who hadn't been exposed to digital fabrication before, this wasn't just a new tool. It was a new way of thinking about the relationship between ideas and the physical world.",
      },
    ],

    // Removed redundant intro - outcomes are self-explanatory

    // Memory Match Game for Outcomes
    memoryMatchGame: {
      pairs: [
        {
          cardA: "🤖",
          cardB: "Doodle bot",
          explanation:
            "Every student assembled a working Budmen Scribbler from scratch on Day 1, a motorized doodle bot they took home. Low barrier, high delight, and immediate proof that they can build real things.",
        },
        {
          cardA: "🧀",
          cardB: "Dragon",
          explanation:
            "A student attempted to build a complex form using only pressurized cheese. The spectacular, hilarious failure became the camp's best lesson in material properties, structural integrity, and the value of laughing through failure.",
        },
        {
          cardA: "🏺",
          cardB: "Filament",
          explanation:
            "On Day 3, every student created an original concept in clay, then used plastic extrusion devices to build it in real 3D printing filament, acting as human 3D printers. The creations were fantastic.",
        },
        {
          cardA: "📸",
          cardB: "3D model",
          explanation:
            "Students saw how ordinary cameras can capture physical objects as digital data through photogrammetry and compared the results with infrared scanning to understand the strengths and limitations of each method.",
        },
        {
          cardA: "💥",
          cardB: "Data",
          explanation:
            "The layering challenge on Day 2 was designed to produce failure, and it did, gloriously. But because reflection was built into every stage, students didn't experience failure as defeat. They experienced it as data.",
        },
        {
          cardA: "🤝",
          cardB: "Mentor",
          explanation:
            "The four Hillside mentors weren't passive chaperones. They were active learners alongside the students. When adults model genuine curiosity and empathy, the result is children who are resilient, confident, and engaged.",
        },
        {
          cardA: "⚛️",
          cardB: "Bits",
          explanation:
            "The big idea of the camp: physical objects can become digital information, and digital information can become physical objects. For students who hadn't been exposed to digital fabrication before, this was a new way of thinking about the relationship between ideas and the physical world.",
        },
        {
          cardA: "❓",
          cardB: "Discovery",
          explanation:
            "Every day started with a question no one could google the answer to. From 'Can everything be made from layers?' to hands-on experiments that answered it, the Creative Discovery Method turned curiosity into understanding.",
        },
      ],
    },

    outcomes: [
      "Students can assemble functional robots from basic components and troubleshoot them",
      "Students can analyze material properties and explain structural trade-offs",
      "Students can compare different digital capture methods and choose appropriate techniques",
      "Students can translate physical concepts into digital designs and back to physical objects",
      "Students can iterate designs based on failures and articulate what they learned",
      "Students can apply the Creative Discovery Method to investigate new problems",
    ],

    whatsNext:
      "We recommend building on this foundation with a follow-up program that goes deeper into one branch of the workflow: either advanced scanning techniques or design-for-manufacturing. The Hillside mentors expressed strong interest in continuing the partnership, and the students' engagement suggests they'd return eagerly. The cheese whiz dragon deserves a permanent place in the Budmen mythology.",
  },

  // From Atoms to Layers - SUNY Poly Spring Break 3-day camp
  "sunypoly-apr2026": {
    slug: "sunypoly-apr2026",
    password: "layers2026",
    heroImage: "/images/SUNYPOLY-Apr26-1.webp", // Hero image - lab or classroom scene
    heroOverlay: "medium",
    program: {
      name: "From Atoms to Layers",
      tagline: "Exploring the digital fabrication revolution together!",
      partner: "SUNY Polytechnic Institute — Pre-Collegiate Outreach",
      dates: "April 7–9, 2026",
      location: "SUNY Poly Campus",
      studentsServed: 15,
      daysDelivered: 3,
      reportDate: "April 2026",
      partnerOrg: "ICAN",
    },
    // Custom stats for this report
    customStats: [
      { number: 15, label: "Students" },
      { number: 3, label: "Days" },
      { number: 6, label: "Supporting Adults" },
      { number: 1, label: "Intuitive Fixture Maker" },
    ],
    drivingQuestion:
      "What happens when students discover that a 3D printer doesn't just make objects, it's a revolution in access?",
    overview:
      "From Atoms to Layers was a three-day Creative Discovery camp designed to help students understand the revolutionary power of digital fabrication: translating the physical world into digital information and then turning that information back into physical form. Fifteen students from ICAN experienced additive manufacturing, 3D scanning, and the revolution of access that 3D printing provides — all grounded in the Creative Discovery Method.",

    // Images from SUNYPOLY-Apr26 photo set
    images: [
      {
        src: "/images/SUNYPOLY-Apr26-1.webp",
        caption: "Students exploring layered fabrication techniques",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Apr26-2.webp",
        caption:
          "Learning to appreciate material behavior with expanding foam constructions",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Apr26-3.webp",
        caption:
          "Discovering how to balance conditions of time, heat, pressure and material to produce a part",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Apr26-4.webp",
        caption: "Comparing machine made to hand made layering techniques",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Apr26-5.webp",
        caption:
          "Student using a 3D extrusion pen to produce a hand 3d printed part",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Apr26-6.webp",
        caption: "Students creating their own designs with 3D printing tools",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Apr26-7.webp",
        caption: "Deep focus while 3D printing objects and prototypes",
        aspect: "3/2",
      },
      {
        src: "/images/SUNYPOLY-Apr26-8.webp",
        caption:
          "A display of additively manufactured objects for students to explore",
        aspect: "3/2",
      },
    ],

    // Connections Game for Spring Break
    connectionsGame: {
      groups: [
        {
          id: "scanning-tech",
          name: "3D Scanning Technologies",
          cards: ["Infrared sensor", "Camera", "3D scanner", "Photos"],
          color: "#4ECDC4",
          difficulty: 1,
          insight:
            "Students experienced two different methods for turning physical objects into digital data: infrared scanning and photogrammetry. One student volunteer was captured with each method, and both scans were displayed side by side on the projector screen. The class analyzed the strengths and weaknesses of each technology together.",
        },
        {
          id: "materials-extrusion",
          name: "Materials for Extrusion",
          cards: ["Clay", "Cheese whiz", "Hot glue", "Shaving cream"],
          color: "#FF6B6B",
          difficulty: 2,
          insight:
            "Students got their hands dirty with four wildly different materials, each teaching unique lessons: shaving cream expands after extrusion, hot glue needs cooling time between layers, cheese whiz doesn't extrude in straight lines and has air pockets, and clay maintains its shape best. These material discoveries became the foundation for understanding 3D printing constraints.",
        },
        {
          id: "3d-printer-components",
          name: "3D Printer Components",
          cards: ["Extruder", "Filament", "Layer Height", "Infill"],
          color: "#95E77E",
          difficulty: 3,
          insight:
            "These aren't just vocabulary words - students physically experienced each concept. They acted as human extruders, felt real filament, saw how layer height affects strength and detail, and discovered why infill patterns matter when their cheese whiz structures collapsed. By Day 3, they understood these components from hands-on experience.",
        },
        {
          id: "creative-discovery",
          name: "Creative Discovery Process",
          cards: [
            "Prototyping",
            "Manufacturing",
            "Iteration",
            "Problem-solving",
          ],
          color: "#FFE66D",
          difficulty: 4,
          insight:
            "The Creative Discovery Method in action: students prototyped with unusual materials, understood manufacturing constraints through failure, iterated their designs based on what they learned, and solved real problems like the student who invented a fixture to support their cheese whiz hole. This is how real innovation happens.",
        },
      ],
    },

    dayByDay: [
      {
        day: 1,
        title: "Applied Curiosity + The Joy of Asking",
        date: "Tuesday, April 7",
        content:
          "We opened with an introduction to Applied Curiosity and the Creative Discovery Method, not as a lecture, but as an experience. Students dove into the Mystery Box Challenge, a collaboration game that builds observation and hypothesis skills by actively challenging assumptions. The items inside ranged from drinking straws to glue sticks to Smarties candy to pencil eraser tops. Most groups were hesitant in the beginning, as they always are. But once they started asking questions that opened or closed doors to their assumptions, the energy in the room changed. We paused to talk about what kinds of questions help them and what kinds don't — subjective questions, for example, tend to close doors rather than open them. The Mystery Box isn't a game about figuring out what's inside the box. It's a game about learning how to ask questions, a skill they can take with them wherever they go.",
        keyMoment:
          "The shift came when students realized the point wasn't to guess right, it was to ask better. They picked up the strategy of decision trees and started using them to set themselves up for success — asking questions about environment, about where an item might get used. The best part was watching them challenge their own assumptions as they went.",
      },
      {
        day: 2,
        title: "Why 3D Printing Is a Revolution",
        date: "Wednesday, April 8",
        content:
          "Day 2 opened with a class discussion: what is a revolution, and what makes something a revolution? From there we moved into why additive manufacturing matters — not just as a technology, but as a paradigm shift. We explored the democratization of technology, the liberation of access, and the fundamental transition of physical objects from atoms to bits. Then students got their hands dirty, literally. Using cheese whiz, hot glue, clay, and shaving cream, they attempted to recreate four very different objects with distinct structural challenges. At each stage, students reflected on what went well, what failed, and how they'd approach it differently, embedding the iterative process as a practice, not just a concept. Along the way, students discovered things the materials taught them — that shaving cream expands after it's extruded, that a nozzle's flat side can be turned into an advantage, that hot glue takes a while to cool before a new layer can go on top, that cheese whiz doesn't extrude in straight lines and has air pockets inside. All valuable insights about materials and constraints.",
        keyMoment:
          "Sometimes students do things and they don't know why. One student was recreating an object with a hole in it using cheese whiz, and realized the cheese whiz would sag in that area. Intuitively, they placed the cheese whiz nozzle cap where the hole should be and extruded around it. When it came time to present, they were embarrassed to show it. But once we clapped, our energy was infectious — and they were proud to display it. We introduced the terms jig and fixture, and that student learned they had just invented one.",
      },
      {
        day: 3,
        title: "From Inspiration to Digital Creation",
        date: "Thursday, April 9",
        content:
          "We started the final day with a tour of the SUNY Poly 3D printing lab. Students walked past rows of 3D printers in action and saw how different materials produce different kinds of parts. That tour was really about access — letting them see themselves on a college campus, see the technology in real use, and walk away with the understanding that they can do this too. Back in the classroom, we opened a conversation about inspiration: where it comes from, what they're drawn to, what they'd want to make if they could make anything. From there we moved into a live 3D scanning demonstration. One student volunteer was captured with infrared scanning and another with photogrammetry, and both scans were displayed side by side on the projector screen. The class analyzed the strengths and weaknesses of each technology together, discussing what each one sees (and misses) in the physical world. Then students created their own original concepts in clay, and used plastic extrusion devices to build their objects out of real 3D printing filament, acting as human 3D printers. What they made was personal — ET figures, snakes, flowers, tea kettles — the first thing each of them wanted to hold in their hands.",
        keyMoment:
          "This was the day students opened up with each other. Some had been quiet through the first two days. The 3D pens gave them a way to make things they were genuinely excited about, and in the making, they started telling stories and sharing. They weren't just working next to each other anymore. They were talking to each other.",
      },
    ],

    // Trivia Game for Spring Break
    triviaGame: {
      questions: [
        {
          question:
            "The Budmen education team described this group's engagement level as which of the following?",
          options: [
            "Wild and scattered throughout",
            "Strong but focused — thoughtful, not wild",
            "Quiet the entire three days",
            "Variable depending on the activity",
          ],
          correctIndex: 1,
          insight:
            "The level of engagement was strong but focused. This wasn't a wild group, it was a thoughtful group. They absorbed, they considered, and they built carefully. When the 3D printing pens came out on Day 3, that thoughtfulness became creativity made visible.",
        },
        {
          question:
            "What manufacturing concept did one student accidentally discover when trying to create a hole in their cheese whiz object?",
          options: [
            "Layer adhesion",
            "Support material",
            "Jigs and fixtures",
            "Infill patterns",
          ],
          correctIndex: 2,
          insight:
            "A student intuitively placed the cheese whiz nozzle cap where a hole should be and extruded around it, creating a support structure. When we introduced the terms 'jig' and 'fixture,' that student learned they had just invented one — a perfect example of discovery through experimentation.",
        },
        {
          question:
            "What gave students on Day 3 a way to open up and start sharing stories with each other?",
          options: [
            "The campus tour",
            "The 3D scanning demonstration",
            "Making personal creations with 3D pens",
            "The group reflection session",
          ],
          correctIndex: 2,
          insight:
            "The 3D pens gave quiet students a way to make things they were genuinely excited about — ET figures, snakes, flowers, tea kettles. In the making, they started telling stories and sharing. They weren't just working next to each other anymore; they were talking to each other.",
        },
        {
          question:
            "What role did the supporting adults (ICAN staff and SUNY Poly students) play in the camp's success?",
          options: [
            "They managed logistics and discipline",
            "They observed from the sidelines",
            "They jumped into activities and stayed curious alongside students",
            "They provided technical expertise only",
          ],
          correctIndex: 2,
          insight:
            "The engagement of the adults in the room made all the difference. When the six supporting adults jumped into activities, asked questions, and stayed curious alongside the students, it gave the students permission to do the same. Authentic learning is contagious.",
        },
      ],
    },

    facilitatorObservations: [
      {
        theme: "Thoughtful, Not Wild — And Quietly Opening Up",
        observation:
          "The level of engagement was strong but focused. This wasn't a wild group, it was a thoughtful group. They absorbed, they considered, and they built carefully. When the 3D printing pens came out on Day 3, that thoughtfulness became creativity made visible. Students who had been quiet during the first two days started chatting with each other about the things they were passionate about — ET figures, snakes, flowers, tea kettles — and the variety of passions that surfaced was fascinating to watch.",
      },
      {
        theme: "Adults in the Room Set the Tone",
        observation:
          "The engagement of the adults in the room made all the difference. Three ICAN staff came with the students and already had a real connection with them. Three SUNY Poly student supporters were there as well. When those adults jumped into activities, asked questions, and stayed curious alongside the students, it gave the students permission to do the same. Authentic learning is contagious, and when the adults in the room are modeling it, the students follow.",
      },
      {
        theme: "Showing the Shop Is Showing Access",
        observation:
          "This time we brought in more objects from our shop: a metal injection mold students could feel and inspect, a 3D-printed cavity that gets filled with poured silicone, parts in resin, TPU, and biodegradable filament-based materials, a set of resin teeth scanned the way Invisalign scans a patient, and a 3D-printed scan of a brain. These weren't demos of finished products. They were real-use parts showing 3D printing and scanning at work in dentistry, healthcare, art museums, and a long list of other industries. That kind of exposure opens doors — to conversations, to questions, to opportunities students didn't know existed. The point of bringing the shop into the room is access.",
      },
      {
        theme: "Failure as Fuel",
        observation:
          "The layering fabrication challenge on Day 2 was designed to produce failure, and it did, gloriously. But because reflection was built into every stage, students didn't experience failure as defeat. They experienced it as data. The fixture moment was the cleanest expression of this — a student invented a solution under pressure, doubted it, and then discovered that what they'd done intuitively had a name and a long history in manufacturing.",
      },
    ],

    // Memory Match Game for Spring Break Outcomes
    memoryMatchGame: {
      pairs: [
        {
          cardA: "💡",
          cardB: "Fixture",
          explanation:
            "A student intuitively invented a jig to solve a cheese whiz structural problem, then learned their solution had a name and a long history in manufacturing. Sometimes the best discoveries happen when you don't know you're discovering.",
        },
        {
          cardA: "🧀",
          cardB: "Data",
          explanation:
            "The cheese whiz taught students that materials have personalities: it doesn't extrude in straight lines, has air pockets, and will sag without support. Every failure became data about material constraints.",
        },
        {
          cardA: "🔮",
          cardB: "Discovery",
          explanation:
            "The Creative Discovery Method turned the Mystery Box from a guessing game into a masterclass in asking better questions. Students learned that the point wasn't to guess right, it was to ask better.",
        },
        {
          cardA: "📦",
          cardB: "3D model",
          explanation:
            "Students watched as their classmates were transformed into digital data through infrared and photogrammetry scanning, then analyzed what each technology sees and misses in the physical world.",
        },
        {
          cardA: "👾",
          cardB: "Mentor",
          explanation:
            "Six supporting adults (3 ICAN staff, 3 SUNY Poly students) didn't just supervise — they jumped in, stayed curious, and modeled authentic learning. When adults show genuine engagement, students follow.",
        },
        {
          cardA: "⚛️",
          cardB: "Bits",
          explanation:
            "The revolutionary concept: physical objects can become digital information (atoms to bits) and digital information can become physical objects (bits to atoms). This paradigm shift is what makes 3D printing a revolution in access.",
        },
        {
          cardA: "💫",
          cardB: "Filament",
          explanation:
            "On Day 3, students went from clay concepts to extruding real 3D printing filament, acting as human 3D printers. They made personal objects — ET figures, snakes, flowers, tea kettles — the first things they wanted to hold.",
        },
        {
          cardA: "❓",
          cardB: "Mystery Box",
          explanation:
            "The Mystery Box Challenge taught students to use decision trees, ask questions that open doors rather than close them, and challenge their own assumptions. It's not about what's in the box; it's about learning how to ask.",
        },
      ],
    },

    outcomes: [
      "Students can ask questions that open doors to their assumptions rather than close them",
      "Students have a working understanding of additive manufacturing and the vocabulary that comes with it — infill, perimeters, supports, layer height, filament",
      "Students can analyze material properties and explain structural trade-offs",
      "Students can recognize and invent jigs and fixtures to solve fabrication problems",
      "Students can compare different digital capture methods and choose appropriate techniques",
      "Students can translate physical concepts into digital designs and back to physical objects",
      "Students can iterate designs based on failures and articulate what they learned",
      "Students can apply the Creative Discovery Method to investigate new problems",
    ],

    lookingAhead: {
      intro: "Next Steps",
      actions: [
        "The Budmen Education team truly enjoys collaborating with SUNY Poly's Pre-Collegiate Outreach team, whose commitment to hands-on STEM education and student access aligns perfectly with our Creative Discovery mission. We look forward to continuing this partnership with programs in summer, fall, and throughout 2027 and beyond.",
      ],
    },
  },

  // Add future reports here following the same structure
  // Example:
  // "program-name-2027": {
  //   slug: "program-name-2027",
  //   password: "password2027",
  //   program: { ... },
  //   ...
  // }
};

// Helper function to get report by slug
export const getReportBySlug = (slug) => {
  return REPORTS_CONFIG[slug] || null;
};

// Helper function to get all report slugs (useful for generating routes)
export const getAllReportSlugs = () => {
  return Object.keys(REPORTS_CONFIG);
};

// Helper function to validate password for a report
export const validateReportPassword = (slug, password) => {
  const report = getReportBySlug(slug);
  if (!report) return false;
  return report.password.toLowerCase() === password.trim().toLowerCase();
};
